<template>
    <teleport to="#modals">
        <div class="absolute top-0 left-0 right-0 bottom-0 p-2 pb-10 flex" :class="positionClass" v-if="isVisible">
            <div
                :class="[
                    'z-50 p-6 transition-all duration-300 w-full h-auto',
                    fadeOut ? 'fade-out' : 'fade-in',
                    transitionClass,
                    colorClass,
                    roundedClass,
                    widthClass
                ]"
                :style="{ maxWidth: maxWidth }"
                style="box-shadow: rgba(0, 0, 0, 0.2) 0px 3px 5px -1px, rgba(0, 0, 0, 0.14) 0px 6px 10px 0px, rgba(0, 0, 0, 0.12) 0px 1px 18px 0px;"
            >
                <slot :closeSnackbar="closeSnackbar"></slot>
                <button
                    v-if="!hideCloseButton"
                    class="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    @click="closeSnackbar"
                >&times;</button>
            </div>
        </div>
    </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue';

export default defineComponent({
    name: 'MySnackbar',
    props: {
        modelValue: { type: Boolean, required: true },
        timeout: { type: Number, default: -1 }, // -1 для отключения авто-закрытия
        location: { type: String, default: 'center' }, // 'top', 'center', 'bottom'
        maxWidth: { type: String, default: '672px' },
        width: { type: String, default: '100%' },
        rounded: { type: String, default: 'xl' },
        color: { type: String, default: 'white' },
        transition: { type: String, default: 'slide-y-reverse-transition' },
        vertical: { type: Boolean, default: false },
        hideCloseButton: { type: Boolean, default: false },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const isVisible = ref(props.modelValue);
        const fadeOut = ref(false);

        const closeSnackbar = () => {
            fadeOut.value = true;
            setTimeout(() => {
                emit('update:modelValue', false);
            }, 250);
        }

        watch(
            () => props.modelValue,
            (newValue) => {
                isVisible.value = newValue;
                if (newValue && props.timeout !== -1) {
                    setTimeout(closeSnackbar, props.timeout);
                }
            }
        );

        // Маппинг для позиционирования
        const selfBy: Record<string, string> = {
            left: 'start',
            right: 'end',
            top: 'start',
            bottom: 'end',
        }

        const locationValues = props.location?.split(' ');

        let justifySelf: (string | undefined) = locationValues?.find?.((i) => i === 'left' || i === 'right');
        justifySelf = justifySelf ? selfBy[justifySelf] : undefined;
        let self: (string | undefined) = locationValues?.find?.((i) => i === 'top' || i === 'bottom');
        self = self ? selfBy[self] : undefined;
        const center = locationValues?.includes?.('center');

        // Маппинг для анимаций
        const transitionClasses: Record<string, string> = {
            'slide-y-reverse-transition': 'opacity-100 translate-y-0',
            'slide-y-transition': 'opacity-100 -translate-y-0',
        };

        const positionClass = computed(() => (
            [
                justifySelf || center ? `justify-${justifySelf || 'center'}` : false,
                self || center ? `items-${self || 'center'}` : false,
            ].filter(Boolean).join(' ')
        ));
        const roundedClass = computed(() => `rounded-${props.rounded}`);
        const colorClass = computed(() => `bg-${props.color}`);
        const widthClass = computed(() => `w-[${props.width}]`);
        const transitionClass = computed(() => transitionClasses[props.transition] || 'opacity-100');

        return {
            fadeOut,
            isVisible,
            closeSnackbar,
            positionClass,
            roundedClass,
            colorClass,
            widthClass,
            transitionClass,
        };
    },
});
</script>

<style lang="scss">

.fade-in {
    animation: fadeIn 0.25s ease-out;
}

.fade-out {
    opacity: 0 !important;
    transform: translateY(150px) !important;
    transition: opacity 0.25s ease-out, transform 0.25s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(150px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

</style>
